import api from './client';

// =============================================================================
// Check-in Buddy API — Spec 314 Phase A
//
// Buddy = an existing Helm user, bilateral consent (buddy must accept before
// escalation notifications ever reach them). Purely additive to the existing
// safety check-in escalation ladder -- see docs/specs/implemented/314-checkin-buddy-network.md.
// =============================================================================

export interface CheckinBuddy {
  /** public_id of the checkin_buddies row -- use this for accept/remove calls. */
  id: string;
  status: 'pending' | 'accepted' | 'declined' | 'removed';
  createdAt: string;
  acceptedAt: string | null;
  /** Relative to the caller: did the caller designate the other party ('outgoing'),
   *  or were they designated by the other party ('incoming')? */
  direction: 'outgoing' | 'incoming';
  otherUser: {
    /** public_id (UUID) of the other party -- matches messagingService's user-search `id`. */
    id: string;
    displayName: string;
  };
}

export const checkinBuddyAPI = {
  /** GET /safety/buddies returns a bare array (data: CheckinBuddy[]), NOT
   *  `{ buddies: [...] }` -- verified directly against safetyRoutes.ts's
   *  handler (`res.json({ success: true, data: buddies })` where `buddies`
   *  is the array checkinBuddyService.listBuddies() returns). */
  list: () => api.get<{ success: true; data: CheckinBuddy[] }>('/safety/buddies'),

  /** `buddyUserId` is the target's public_id (UUID) -- the same `id` shape
   *  messagingService's user-search endpoint returns. */
  designate: (buddyUserId: string) =>
    api.post<{ success: true; data: CheckinBuddy }>('/safety/buddies', { buddyUserId }),

  /** `id` is the checkin_buddies row's public_id. Only the invited party
   *  (direction: 'incoming') may accept -- enforced server-side. */
  accept: (id: string) =>
    api.post<{ success: true; data: CheckinBuddy }>(`/safety/buddies/${id}/accept`),

  /** Either party may remove the relationship at any time, pending or accepted. */
  remove: (id: string) => api.delete<{ success: true; data: { removed: true } }>(`/safety/buddies/${id}`),
};
